/**
 * WorkReportFormModal state logic — shapes, prefill and payload.
 *
 * Extracted verbatim from WorkReportFormModal (REPORT-PEOPLE, Regel 48)
 * before multi-person capture was added: the modal was at 478 lines and
 * about to gain a second person field plus the person-time preview.
 * Nothing here renders — it is the form's data, not its markup.
 *
 * Pure move. Same shapes, same order, same coercions, so the only
 * behavioural difference is which file the code lives in.
 */
import { ENTITY_IDS_FIELDS, manualIdsByField } from "../../entityLinks/multiEntityLinks";
import {
  dateTimeLocalToIso,
  isoToDateInputValue,
  isoToTimeInputValue,
} from "./workReportConstants";

/** Ceiling on people per capture, mirroring
 * ``WORK_REPORT_MAX_BATCH_PEOPLE`` in the backend schema. Enforced in
 * both places on purpose: the form is a convenience, the API is the
 * contract. */
export const MAX_BATCH_PEOPLE = 20;

export function todayDateInputValue() {
  return isoToDateInputValue(new Date().toISOString());
}

export const EMPTY_FORM = {
  date: "",
  startTime: "",
  endTime: "",
  title: "",
  description: "",
  report_type: "general",
  // CAT1: the area of work, independent of ``report_type``. Required for
  // NEW reports in the form; the API stays nullable.
  activity_category: "",
  // REPORT-PEOPLE: create mode captures PEOPLE, plural. Edit mode uses
  // `assigned_to_user_id` below — one stored report is one person's
  // booking and stays that way.
  assigned_to_user_ids: [],
  assigned_to_user_id: "",
  // ``task_id`` stays SINGULAR: it is an owner relation, not one of the
  // seven entity links. A report belongs to at most one task, and
  // multiple assignment would count its minutes under two of them.
  task_id: "",
  // ME3a: the seven entity links are MANUAL id SETS now.
  tenant_ids: [],
  tenancy_ids: [],
  room_ids: [],
  unit_ids: [],
  property_ids: [],
  application_ids: [],
  landlord_ids: [],
};

/** Links a caller may prefill in create mode (C1.1 + C3). Order is part
 * of the contract: `defaultsKey` in the modal serializes values in
 * exactly this sequence and the reset effect reads them back
 * positionally. */
export const CREATE_DEFAULT_KEYS = [
  "task_id",
  "tenancy_id",
  "tenant_id",
  "application_id",
  "unit_id",
  "landlord_id",
  "property_id",
  "room_id",
];

export function reportToForm(report) {
  return {
    date: isoToDateInputValue(report.started_at),
    startTime: isoToTimeInputValue(report.started_at),
    endTime: isoToTimeInputValue(report.ended_at),
    title: report.title || "",
    description: report.description || "",
    report_type: report.report_type || "general",
    activity_category: report.activity_category || "",
    assigned_to_user_id: report.assigned_to_user_id || "",
    // A stored report names exactly one person; the plural field stays
    // empty so nothing in edit mode can read a group out of it.
    assigned_to_user_ids: [],
    task_id: report.task_id || "",
    // ME3a: MANUAL links only — a derived link loaded as a selection
    // would be persisted as a deliberate choice on the next save.
    ...manualIdsByField(report),
  };
}

/** The link half of a create-mode reset, rebuilt from the positional
 * `defaultsKey` string the modal memoises. */
export function createDefaultLinks(defaultsKey) {
  const values = String(defaultsKey ?? "").split("|");
  const links = {};
  CREATE_DEFAULT_KEYS.forEach((key, i) => {
    const value = values[i] || "";
    if (key === "task_id") {
      links[key] = value;
      return;
    }
    // ME3a: a caller still prefills ONE id per type (opening the form
    // from a task detail page); it becomes that type's manual set.
    links[`${key}s`] = value ? [value] : [];
  });
  return links;
}

/** Shared work fields — everything except who did it. Both payload
 * builders below start from this, so the single and the batch request
 * can never describe the work differently. */
function workFields(form) {
  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    report_type: form.report_type,
    activity_category: form.activity_category || null,
    started_at: dateTimeLocalToIso(form.date, form.startTime),
    ended_at: dateTimeLocalToIso(form.date, form.endTime),
    // Owner relation, unchanged: empty string means "not set" on create
    // and "explicitly unlinked" on update, both null on the wire.
    task_id: form.task_id || null,
    // ME3a: the seven plural sets, and NO singular ``*_id`` keys for
    // them. `[]` clears that type's manual links; `null` would be a 422.
    ...ENTITY_IDS_FIELDS.reduce((acc, field) => {
      acc[field] = Array.isArray(form[field]) ? form[field] : [];
      return acc;
    }, {}),
  };
}

/** Build the single-report API payload. Server-owned fields are
 * intentionally absent; empty-string links become null (unlink / not
 * set). Unchanged contract — this is what POST /work-reports and every
 * PATCH have always received. */
export function formToPayload(form) {
  return {
    ...workFields(form),
    assigned_to_user_id: form.assigned_to_user_id || null,
  };
}

/** Build the multi-person payload for POST /work-reports/batch.
 *
 * Deliberately NOT a superset of `formToPayload`: the singular
 * `assigned_to_user_id` is absent, because the batch schema rejects it
 * with a 422 rather than silently ignoring it. */
export function formToBatchPayload(form) {
  return {
    ...workFields(form),
    assigned_to_user_ids: selectedUserIds(form),
  };
}

/** The chosen people, normalised the same way the server does: trimmed,
 * blanks dropped, de-duplicated, first occurrence wins.
 *
 * Doing it here as well is not redundancy for its own sake — the
 * person-time preview must count exactly what will be created, and a
 * preview that says "4 Personenbuchungen" for a list the server folds
 * into 3 would be a lie told before the request is even sent. */
export function selectedUserIds(form) {
  const raw = Array.isArray(form.assigned_to_user_ids)
    ? form.assigned_to_user_ids
    : [];
  const seen = new Set();
  const out = [];
  raw.forEach((item) => {
    const key = String(item ?? "").trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });
  return out;
}
